import { roundDown } from "@/helpers/utility/MathHelper";
import type { SceneTreeItemSize } from "@editor/items/scene/SceneTreeItem";

const GRID_SIZE = 10;

/**
 * Build a map of all items on the field, ordered by coordinates.
 * The collision map can be used to quickly find items that are in the same grid cell.
 */
export function buildItemCollisionMap(netItems: Record<string, SceneTreeItemSize>) {
  const collisionMap = new Map<number, Map<number, SceneTreeItemSize[]>>();

  for (const uid in netItems) {
    const item = netItems[uid];
    
    for (let x = item.x; x <= item.x + item.width; x += GRID_SIZE) {
      if (!collisionMap.has(x)) collisionMap.set(x, new Map());
      const xMap = collisionMap.get(x);
      if (!xMap) throw new Error("Map should exist");
      for (let y = item.y; y <= item.y + item.height; y += GRID_SIZE) {
        if (!xMap.has(y)) xMap.set(y, []);
        xMap.get(y)?.push(item);
      }
    }
  }

  return collisionMap;
}

/**
 * Returns all items that are in the same grid cells as the given coordinates.
 */
export function getCollisions(
  collisionMap: CollisionMap,
  left: number,
  top: number,
  right: number,
  bottom: number
) {
  const collisions = new Set<SceneTreeItemSize>();
  const xStart = roundDown(left / GRID_SIZE) * GRID_SIZE;
  const yStart = roundDown(top / GRID_SIZE) * GRID_SIZE;

  for (let x2 = xStart; x2 <= right; x2 += GRID_SIZE) {
    const xMap = collisionMap.get(x2);
    if (!xMap) continue;

    for (let y2 = yStart; y2 <= bottom; y2 += GRID_SIZE) {
      const yMap = xMap.get(y2);
      if (!yMap) continue;
      for (const item of yMap) {
        collisions.add(item);
      }
    }
  }
  return collisions;
}

/**
 * Returns any first item that is in the same grid cells as the given coordinates.
 */
export function getCollision(
  collisionMap: CollisionMap,
  left: number,
  top: number,
  right: number,
  bottom: number,
  ignoredUIDs: string[] = []
) {
  const xStart = roundDown(left / GRID_SIZE) * GRID_SIZE;
  const yStart = roundDown(top / GRID_SIZE) * GRID_SIZE;

  for (let x2 = xStart; x2 <= right; x2 += GRID_SIZE) {
    const xMap = collisionMap.get(x2);
    if (!xMap) continue;

    for (let y2 = yStart; y2 <= bottom; y2 += GRID_SIZE) {
      const yMap = xMap.get(y2);
      if (!yMap) continue;
      for (const item of yMap) {
        if (ignoredUIDs.includes(item.uid)) continue;
        return item;
      }
    }
  }
  return undefined;
}

export type CollisionMap = ReturnType<typeof buildItemCollisionMap>;
